import { isEnabled } from "@/utils/featureFlags";
/** This is to prevent a circular import. We can remove this once we have
 * selectors separated from their reducers. */
function getChatState(state) {
    return state["chatGPT"];
}
export function getConversationMemoryEnabled(state) {
    const storedValue = localStorage.getItem("conversationMemoryEnabled");
    const retStoredVal = storedValue !== null && isEnabled("chat-conversation-memory") ? JSON.parse(storedValue) : undefined;
    return getChatState(state).conversationMemoryEnabled ?? retStoredVal ?? false;
}
export function getConversationMemoryMessageLimit(state) {
    const storedValue = localStorage.getItem("conversationMemoryMessageLimit");
    const retStoredVal = storedValue !== null ? JSON.parse(storedValue) : undefined;
    return getChatState(state).conversationMemoryMessageLimit ?? retStoredVal ?? 10;
}
export function getConversationMemoryIncludeReferenceData(state) {
    const storedValue = localStorage.getItem("conversationMemoryIncludeReferenceData");
    const retStoredVal = storedValue !== null ? JSON.parse(storedValue) : undefined;
    return getChatState(state).conversationMemoryIncludeReferenceData ?? retStoredVal ?? false;
}
/* Multi-Session Memory */
export function getMultiSessionMemoryEnabled(state) {
    const storedValue = localStorage.getItem("multiSessionMemoryEnabled");
    const retStoredVal = storedValue !== null && isEnabled("chat-conversation-memory") ? JSON.parse(storedValue) : undefined;
    return getChatState(state).multiSessionMemoryEnabled ?? retStoredVal ?? false;
}
export function getMultiSessionMemoryCreationEnabled(state) {
    const storedValue = localStorage.getItem("multiSessionMemoryCreationEnabled");
    const retStoredVal = storedValue !== null && isEnabled("chat-conversation-memory") ? JSON.parse(storedValue) : undefined;
    return getChatState(state).multiSessionMemoryCreationEnabled ?? retStoredVal ?? false;
}
export const DEFAULT_MEMORY_AUGMENTATION_PROMPT_TEMPLATE = `Integrate these relevant memories in the analysis as required
RELEVANT MEMORIES:
{relevant_memories}

User Prompt: {input}`;
export const DEFAULT_USER_PERSONA_CREATION_PROMPT_TEMPLATE = `You are a user persona generator.
Given an agent's conversation history, add any facts about user's preferences to long term memory.
IMPORTANT: Output ONLY valid JSON. Do not output any blank lines, extra whitespace, or trailing newlines outside the JSON object. Do not pad fields with whitespace. Do not add commentary.
Here's an example memory:
{{"memory": "User is vegetarian and has a nut allergy."}}
Example of empty memory case:
{{"memory": null}}
**SIMILAR RELEVANT MEMORIES**
{retrieved_memories}
**GUIDELINES**
1. Only include facts and preferences that will be useful for a future agent.
2. Restrict the memory to 2 short sentences.
3. Memory strings must be brief.
4. If the memory already exists in the **SIMILAR RELEVANT MEMORIES** section, return a null memory.
**CONVERSATION HISTORY**
Input: {input}
Agent's Response: {response}
`;
export const DEFAULT_MEMORY_CREATION_PROMPT_TEMPLATE = `You are a analysis context generator.
Given an agent's conversation history, add any facts about context and conclusions of the analysis to long term memory.
IMPORTANT: Output ONLY valid JSON. Do not output any blank lines, extra whitespace, or trailing newlines outside the JSON object. Do not pad fields with whitespace. Do not add commentary.
Here's an example memory:
{{"memory":"User analyzed customer churn by cohort and concluded churn is highest in the first 30 days due to onboarding friction. Recommendation was to improve initial product walkthroughs."}}
Example of empty memory case:
{{"memory": null}}
**SIMILAR RELEVANT MEMORIES**
{retrieved_memories}
**GUIDELINES**
1. Only include facts and preferences that will be useful for a future agent.
2. Restrict the memory to 2 short sentences.
3. Memory strings must be brief.
4. If the memory already exists in the **SIMILAR RELEVANT MEMORIES** section, return a null memory.
**CONVERSATION HISTORY**
Input: {input}
Agent's Response: {response}`;
const DEFAULT_MEMORY_CREATION_TYPES = {
    USER_PERSONA: {
        id: "USER_PERSONA",
        topic: "user_persona",
        enableAugmentation: true,
        retrievalScope: ["topic"],
        enableCreation: true,
        label: "My Personal Details:",
        description: "What the Agent remembers about me",
        creationPromptTemplate: DEFAULT_USER_PERSONA_CREATION_PROMPT_TEMPLATE
    },
    AGENT_TEMPLATE: {
        id: "AGENT_TEMPLATE",
        topic: "agent_template",
        enableAugmentation: false,
        retrievalScope: ["request_type", "artifact_id"],
        enableCreation: false,
        label: "Agent Template:",
        description: "What the Agent remembers about this Agent",
        creationPromptTemplate: DEFAULT_MEMORY_CREATION_PROMPT_TEMPLATE
    }
};
export const RETRIEVAL_SCOPE_OPTIONS = [
    {
        label: "By Agent",
        value: "request_type"
    },
    {
        label: "By Agent and Data",
        value: "usecase_and_data"
    },
    {
        label: "Global",
        value: "topic"
    }
];
export function getRetrievalScopeOptions(state) {
    return RETRIEVAL_SCOPE_OPTIONS;
}
export function getMultiSessionMemoryTypes(state) {
    const storedValue = localStorage.getItem("multiSessionMemoryTypes");
    const retStoredVal = storedValue !== null && isEnabled("chat-conversation-memory") ? JSON.parse(storedValue) : undefined;
    return getChatState(state).multiSessionMemoryTypes ?? retStoredVal ?? DEFAULT_MEMORY_CREATION_TYPES;
}
export function getMultiSessionMemoryTypeById(state, id) {
    const memoryCreationTypes = getMultiSessionMemoryTypes(state);
    return memoryCreationTypes?.[id];
}
export function getMultiSessionMemoryCount(state) {
    const storedValue = localStorage.getItem("multiSessionMemoryCount");
    const retStoredVal = storedValue !== null ? JSON.parse(storedValue) : undefined;
    return getChatState(state).multiSessionMemoryCount ?? retStoredVal ?? 10;
}
export function getMemoryAugmentationPromptTemplate(state) {
    const storedValue = localStorage.getItem("memoryAugmentationPromptTemplate");
    const retStoredVal = storedValue !== null && isEnabled("chat-conversation-memory") ? JSON.parse(storedValue) : undefined;
    return (getChatState(state).memoryAugmentationPromptTemplate ?? retStoredVal ?? DEFAULT_MEMORY_AUGMENTATION_PROMPT_TEMPLATE);
}
// TODO: Remove this as it's being replaced with individual values on memory type configs. Still currently used for agent setup as it hasn't been updated yet.
export function getMemoryCreationPromptTemplate(state) {
    const storedValue = localStorage.getItem("memoryCreationPromptTemplate");
    const retStoredVal = storedValue !== null && isEnabled("chat-conversation-memory") ? JSON.parse(storedValue) : undefined;
    return getChatState(state).memoryCreationPromptTemplate ?? retStoredVal ?? DEFAULT_MEMORY_CREATION_PROMPT_TEMPLATE;
}
